import React, { useState, useEffect, useCallback } from 'react';
import { X, Zap, Check, ChevronDown, ChevronUp, Filter, Download, Upload, Star, Wifi, Home, Briefcase, Sliders } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { getNetworkOperator, getRecommendedPackages } from './api';
import { useClarityModalTracking } from '../../hooks/useClarityModalTracking';
import { trackEvent, trackJourneyStep, trackConversion } from '../../config/clarity';

const PackageComparisonModal = ({
  isOpen,
  onClose,
  selectedOperator,
  allOperators = [],
  isSideBySideLayout = false,
  // Filter props
  selectedOperators = [],
  sortBy = 'price',
  sortDirection = 'asc',
  speedFilter = [0, 1000],
  priceFilter = [0, 2000],
  // Signup props
  openSignup = () => {},
  isUsingGeolocation = false
}) => {
  const [packages, setPackages] = useState([]);
  const [filteredPackages, setFilteredPackages] = useState([]);
  const [filterExpanded, setFilterExpanded] = useState(false);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 768);
  const [homePackagesExpanded, setHomePackagesExpanded] = useState(true);
  const [businessPackagesExpanded, setBusinessPackagesExpanded] = useState(true);
  
  // Check if we're on mobile
  const isMobile = windowWidth < 768;
  
  // Handle window resize
  const handleResize = useCallback(() => {
    setWindowWidth(window.innerWidth);
  }, []);
  
  // Set up resize listener
  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  // Track modal open/close
  const { trackAction } = useClarityModalTracking('comparison_modal', isOpen, {
    operatorCount: selectedOperators.length,
    isSideBySideLayout
  });

  // Fetch packages for all selected operators
  useEffect(() => {
    if (isOpen && selectedOperators.length > 0) {
      const allPackages = [];
      
      selectedOperators.forEach(operatorIdentifier => {
        // Parse the operator identifier to get name and connection type
        const [operatorName, connectionType = 'FTTH'] = operatorIdentifier.split(':');
        
        // Get packages for this operator and connection type
        const { packages: operatorPackages } = getRecommendedPackages(operatorName, connectionType);
        
        // Add operator name and connection type to each package
        const packagesWithOperator = operatorPackages.map(pkg => ({
          ...pkg,
          operatorName,
          connectionType,
          operator: getNetworkOperator(operatorName)
        }));
        
        allPackages.push(...packagesWithOperator);
      });
      
      setPackages(allPackages);
    }
  }, [isOpen, selectedOperators]);

  // Track comparison view when packages are loaded
  useEffect(() => {
    if (isOpen && filteredPackages.length > 0) {
      // Track the comparison view event
      trackEvent('comparison_view', {
        packages: filteredPackages.map(p => p.operatorName).join(','),
        packageCount: filteredPackages.length,
        homePackageCount: filteredPackages.filter(p => p.connectionType !== 'FTTB').length,
        businessPackageCount: filteredPackages.filter(p => p.connectionType === 'FTTB').length,
        sortBy,
        sortDirection
      });
      
      // Track journey step
      trackJourneyStep('package_selection', 'comparing_packages', {
        packageCount: filteredPackages.length,
        operators: selectedOperators.map(op => op.split(':')[0]).join(',')
      });
    }
  }, [isOpen, filteredPackages, sortBy, sortDirection, selectedOperators]);

  // Apply filters and sorting
  useEffect(() => {
    if (packages.length > 0) {
      let filtered = [...packages];
      
      // Apply speed filter
      filtered = filtered.filter(pkg => {
        const speedMbps = pkg["Download speed (kbps)"] / 1024;
        return speedMbps >= speedFilter[0] && speedMbps <= speedFilter[1];
      });
      
      // Apply price filter
      filtered = filtered.filter(pkg => {
        return pkg.Price >= priceFilter[0] && pkg.Price <= priceFilter[1];
      });
      
      // Apply sorting
      filtered.sort((a, b) => {
        if (sortBy === 'price') {
          return sortDirection === 'asc' ? a.Price - b.Price : b.Price - a.Price;
        } else if (sortBy === 'speed') {
          return sortDirection === 'asc'
            ? a["Download speed (kbps)"] - b["Download speed (kbps)"]
            : b["Download speed (kbps)"] - a["Download speed (kbps)"];
        } else if (sortBy === 'type') {
          // Sort by connection type (FTTH first, then FTTB) and then by symmetry
          const aType = a.connectionType || 'FTTH';
          const bType = b.connectionType || 'FTTH';
          
          // Check if connections are symmetrical
          const aSymmetrical = a["Download speed (kbps)"] === a["Upload speed (kbps)"];
          const bSymmetrical = b["Download speed (kbps)"] === b["Upload speed (kbps)"];
          
          // First sort by connection type
          if (aType !== bType) {
            return sortDirection === 'asc'
              ? aType.localeCompare(bType)
              : bType.localeCompare(aType);
          }
          
          // Then sort by symmetry
          if (aSymmetrical !== bSymmetrical) {
            return sortDirection === 'asc'
              ? (aSymmetrical ? -1 : 1)
              : (bSymmetrical ? -1 : 1);
          }
          
          // If both have same type and symmetry, sort by speed
          return sortDirection === 'asc'
            ? a["Download speed (kbps)"] - b["Download speed (kbps)"]
            : b["Download speed (kbps)"] - a["Download speed (kbps)"];
        }
        return 0;
      });
      
      setFilteredPackages(filtered);
    }
  }, [packages, sortBy, sortDirection, speedFilter, priceFilter]);

  // No need for toggle operator or handle sort change functions as they're now handled in the sidebar

  // Format speed for display
  const formatSpeed = (speedKbps) => {
    const speedMbps = speedKbps / 1024;
    return speedMbps >= 1000 ? `${(speedMbps / 1000).toFixed(1)} Gbps` : `${speedMbps} Mbps`;
  };

  // Determine if a package is suitable for specific use cases
  const getUsageProfile = (pkg) => {
    const downloadSpeed = pkg["Download speed (kbps)"] / 1024; // Convert to Mbps
    const uploadSpeed = pkg["Upload speed (kbps)"] / 1024; // Convert to Mbps
    
    const profiles = [];
    
    // Streaming profile
    if (downloadSpeed >= 100) {
      profiles.push('Great for Streaming');
    } else if (downloadSpeed >= 50) {
      profiles.push('Good for Streaming');
    }
    
    // Gaming profile
    if (downloadSpeed >= 50 && uploadSpeed >= 20) {
      profiles.push('Great for Gaming');
    }
    
    // Work from home profile
    if (uploadSpeed >= 20 && downloadSpeed >= 50) {
      profiles.push('Perfect for WFH');
    } else if (uploadSpeed >= 10 && downloadSpeed >= 25) {
      profiles.push('Good for WFH');
    }
    
    // If symmetrical connection
    if (downloadSpeed === uploadSpeed && downloadSpeed >= 100) {
      profiles.push('Symmetrical');
    }
    
    return profiles.length > 0 ? profiles[0] : null;
  };

  // Animation variants
  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { duration: 0.3 }
    },
    exit: { 
      opacity: 0,
      transition: { duration: 0.3, delay: 0.1 }
    }
  };
  
  const modalVariants = {
    hidden: { 
      opacity: 0
    },
    visible: { 
      opacity: 1,
      transition: { 
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }
    },
    exit: { 
      opacity: 0,
      transition: { 
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };

  // Helper function to render a package card
  const renderPackageCard = (pkg, index) => {
    const downloadSpeed = pkg["Download speed (kbps)"] / 1024;
    const uploadSpeed = pkg["Upload speed (kbps)"] / 1024;
    const usageProfile = getUsageProfile(pkg);
    const isSymmetrical = downloadSpeed === uploadSpeed;
    const isBusinessPackage = pkg.connectionType === 'FTTB';
    
    return (
      <div
        key={index}
        className={`bg-gray-800 rounded-xl overflow-hidden border ${
          isBusinessPackage
            ? 'border-blue-500/30 shadow-lg shadow-blue-500/10' // Business packages
            : usageProfile
              ? 'border-purple-500/30 shadow-lg shadow-purple-500/10' // Home packages with usage profile
              : 'border-purple-500/20 border-opacity-50' // Home packages without usage profile
        }`}
      >
        {/* Package Type Badge */}
        <div className={`w-full py-1 text-center text-xs font-medium ${
          isBusinessPackage
            ? 'bg-blue-600/20 text-blue-400'
            : 'bg-purple-600/20 text-purple-400'
        }`}>
          {isBusinessPackage ? 'BUSINESS' : 'HOME'}
        </div>
        
        {/* Package Header */}
        <div className="p-4 border-b border-gray-700 flex justify-between items-start">
          <div>
            <div className="flex items-center">
              {pkg.operator?.logo ? (
                <img 
                  src={pkg.operator.logo} 
                  alt={pkg.operatorName} 
                  className="h-5 w-5 object-contain mr-2"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.style.display = 'none';
                  }}
                />
              ) : (
                <div className={`h-5 w-5 rounded-full mr-2 flex items-center justify-center ${
                  isBusinessPackage
                    ? 'bg-blue-600/20'
                    : 'bg-purple-600/20'
                }`}>
                  {isBusinessPackage
                    ? <Briefcase className="h-3 w-3 text-blue-400" />
                    : <Home className="h-3 w-3 text-purple-400" />
                  }
                </div>
              )}
              <h3 className="font-medium text-white text-sm">{pkg.operatorName}</h3>
            </div>
            <div className="text-xs text-gray-400 mt-0.5">
              {isBusinessPackage ? 'Business Fibre (FTTB)' : 'Home Fibre (FTTH)'}
            </div>
          </div>
          
          {usageProfile && (
            <div className={`text-xs px-2 py-0.5 rounded-full flex items-center ${
              isBusinessPackage
                ? 'bg-blue-600/20 text-blue-400'
                : 'bg-purple-600/20 text-purple-400'
            }`}>
              <Star className="h-3 w-3 mr-1" />
              {usageProfile}
            </div>
          )}
        </div>
        
        {/* Package Details */}
        <div className="p-4">
          {/* Speed */}
          <div className="flex justify-between items-center mb-4">
            <div>
              <div className="text-2xl font-bold text-white">{formatSpeed(pkg["Download speed (kbps)"])}</div>
              <div className="flex items-center text-xs text-gray-400">
                <Download className="h-3 w-3 mr-1" />
                <span>{downloadSpeed} Mbps</span>
                <Upload className="h-3 w-3 ml-2 mr-1" />
                <span>{uploadSpeed} Mbps</span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-xl font-bold text-white">R{pkg.Price}</div>
              <div className="text-xs text-gray-400">per month</div>
            </div>
          </div>
          
          {/* Connection Type */}
          <div className="mb-4">
            <div className={`flex items-center justify-center p-2 rounded-lg mb-2 ${
              isBusinessPackage
                ? 'bg-blue-600/10 text-blue-400'
                : 'bg-purple-600/10 text-purple-400'
            }`}>
              <div className="text-sm font-medium flex items-center">
                {isBusinessPackage
                  ? <Briefcase className="h-4 w-4 mr-2" />
                  : <Home className="h-4 w-4 mr-2" />
                }
                {isBusinessPackage ? 'Business Fibre' : 'Home Fibre'}
                {isSymmetrical && <span className="ml-1">(Symmetrical)</span>}
              </div>
            </div>
            <div className="h-1.5 bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full ${
                  isBusinessPackage
                    ? 'bg-gradient-to-r from-blue-500 to-cyan-500'
                    : 'bg-gradient-to-r from-purple-500 to-pink-500'
                }`}
                style={{ width: isSymmetrical ? '100%' : '50%' }}
              ></div>
            </div>
          </div>
          
          {/* Features */}
          <div className="space-y-1 mb-4">
            <div className="flex items-center text-xs text-gray-300">
              <Check className="h-3 w-3 text-green-400 mr-2" />
              <span>No throttling or shaping</span>
            </div>
            <div className="flex items-center text-xs text-gray-300">
              <Check className="h-3 w-3 text-green-400 mr-2" />
              <span>Free installation & router</span>
            </div>
            <div className="flex items-center text-xs text-gray-300">
              <Check className="h-3 w-3 text-green-400 mr-2" />
              <span>Month-to-month contract</span>
            </div>
          </div>
          
          {/* Action Button */}
          <button
            className={`w-full py-2 text-white rounded-lg text-sm font-medium transition-colors ${
              isBusinessPackage
                ? 'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700'
                : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700'
            } ${isUsingGeolocation ? 'opacity-50 cursor-not-allowed' : ''}`}
            onClick={() => {
              if (isUsingGeolocation) {
                // Show alert if using geolocation
                alert('Please enter your exact address before signing up. Geolocation is not precise enough for installation.');
                
                // Track geolocation alert
                trackAction('geolocation_alert', {
                  packageName: pkg.Name,
                  packagePrice: pkg.Price,
                  packageSpeed: pkg["Download speed (kbps)"] / 1024,
                  operatorName: pkg.operatorName
                });
              } else {
                // Track package selection (conversion)
                trackConversion('package_selected_after_comparison', {
                  packageName: pkg.Name,
                  packagePrice: pkg.Price,
                  packageSpeed: pkg["Download speed (kbps)"] / 1024,
                  packageUploadSpeed: pkg["Upload speed (kbps)"] / 1024,
                  operatorName: pkg.operatorName,
                  connectionType: pkg.connectionType,
                  isBusinessPackage: pkg.connectionType === 'FTTB',
                  comparedWith: filteredPackages
                    .filter(p => p !== pkg)
                    .map(p => p.operatorName)
                    .join(',')
                });
                
                // Track journey step
                trackJourneyStep('package_selection', 'package_selected', {
                  packageName: pkg.Name,
                  packagePrice: pkg.Price,
                  operatorName: pkg.operatorName
                });
                
                // Open signup modal with this package
                openSignup(pkg, pkg.operatorName);
              }
            }}
            disabled={isUsingGeolocation}
          >
            {isUsingGeolocation ? 'Enter Address First' : 'Select Package'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className={`fixed inset-0 z-60 overflow-hidden ${isSideBySideLayout && !isMobile ? 'left-1/4 right-0 w-auto' : ''}`}>
          {/* Semi-transparent Backdrop that doesn't cover the navigation or sidebar */}
          {!isSideBySideLayout && (
            <motion.div
              className="fixed inset-0 top-16 bg-gray-900/90 backdrop-blur-sm"
              variants={backdropVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            />
          )}
          
          {/* Modal that starts below the navigation and doesn't overlap sidebar */}
          <motion.div
            className={`fixed top-16 bottom-0 flex flex-col ${isSideBySideLayout && !isMobile ? 'left-1/4 right-0' : 'inset-x-0'}`}
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <div className="flex flex-col h-full w-full bg-gray-900">
              {/* Header */}
              <div className="p-4 md:p-6 border-b border-gray-800 flex justify-between items-center bg-gradient-to-r from-gray-900 to-gray-800">
                <h2 className="text-xl font-bold text-white flex items-center">
                  <Zap className="h-5 w-5 text-purple-400 mr-2" />
                  Compare Fibre Packages
                </h2>
                <button
                  className="text-gray-400 hover:text-white p-1 rounded-full hover:bg-gray-800/50 transition-colors"
                  onClick={() => {
                    // Track modal close via X button
                    trackAction('close_button_click');
                    onClose();
                  }}
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              {/* Header with current filter info */}
              <div className="bg-gray-800/50 p-3 md:p-6 border-b border-gray-800">
                <div className={`${isMobile ? 'flex flex-col space-y-3' : 'flex flex-wrap items-center justify-between gap-4'}`}>
                  <div>
                    <h3 className="text-lg font-medium text-white">Fibre Packages</h3>
                    <div className="text-sm text-gray-400 mt-1">
                      Showing {filteredPackages.length} packages from {selectedOperators.length} provider{selectedOperators.length !== 1 ? 's' : ''}
                    </div>
                  </div>
                  
                  {/* Provider Logos - Horizontal scroll on mobile */}
                  <div className={`${isMobile ? 'flex overflow-x-auto pb-2 -mx-3 px-3' : 'flex items-center space-x-3 my-2'}`}>
                    {selectedOperators.map((operatorIdentifier, index) => {
                      const [operatorName] = operatorIdentifier.split(':');
                      const operator = getNetworkOperator(operatorName);
                      return operator?.logo ? (
                        <div key={index} className={`flex flex-col items-center ${isMobile ? 'mr-4 flex-shrink-0' : ''}`}>
                          <img
                            src={operator.logo}
                            alt={operatorName}
                            className="h-8 w-8 object-contain rounded-full bg-gray-700 p-1"
                            title={operatorName}
                          />
                          <span className="text-xs text-gray-400 mt-1 whitespace-nowrap">{operatorName}</span>
                        </div>
                      ) : null;
                    })}
                  </div>
                  
                  <div className="flex items-center text-sm text-gray-400">
                    <span className="mr-2">Sorted by:</span>
                    <span className="text-white font-medium flex items-center">
                      {sortBy === 'price' ? 'Price' : sortBy === 'speed' ? 'Speed' : 'Connection Type'}
                      {sortDirection === 'asc'
                        ? <ChevronUp className="h-3 w-3 ml-1" />
                        : <ChevronDown className="h-3 w-3 ml-1" />
                      }
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Package List */}
              <div className="flex-1 overflow-y-auto p-3 md:p-6 pb-20">
                {/* Filter Button for Mobile */}
                {isMobile && (
                  <button
                    className="mb-4 w-full py-2.5 px-4 bg-purple-600 text-white rounded-lg font-medium flex items-center justify-center"
                    onClick={() => setFilterExpanded(!filterExpanded)}
                  >
                    <Sliders className="h-4 w-4 mr-2" />
                    <span>{filterExpanded ? "Hide Filters" : "Show Filters"}</span>
                  </button>
                )}
                
                {/* Mobile Filters */}
                {isMobile && filterExpanded && (
                  <div className="mb-6 p-4 bg-gray-800 rounded-lg">
                    <h3 className="text-sm font-medium text-white mb-3">Filters</h3>
                    
                    {/* Sort Controls */}
                    <div className="space-y-2 mb-4">
                      <div className="text-xs font-medium text-gray-300">Sort by:</div>
                      <div className="flex rounded-lg overflow-hidden">
                        <button
                          className={`px-3 py-1.5 text-xs font-medium flex items-center ${
                            sortBy === 'price'
                              ? 'bg-purple-600 text-white'
                              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                          }`}
                          onClick={() => {
                            // Track sort change
                            trackAction('sort_change', {
                              newSortBy: 'price',
                              newSortDirection: sortBy === 'price' && sortDirection === 'asc' ? 'desc' : 'asc'
                            });
                            
                            if (sortBy === 'price') {
                              setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                            } else {
                              setSortBy('price');
                              setSortDirection('asc');
                            }
                          }}
                        >
                          Price
                          {sortBy === 'price' && (
                            sortDirection === 'asc'
                              ? <ChevronUp className="h-3 w-3 ml-1" />
                              : <ChevronDown className="h-3 w-3 ml-1" />
                          )}
                        </button>
                        <button
                          className={`px-3 py-1.5 text-xs font-medium flex items-center ${
                            sortBy === 'speed'
                              ? 'bg-purple-600 text-white'
                              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                          }`}
                          onClick={() => {
                            // Track sort change
                            trackAction('sort_change', {
                              newSortBy: 'speed',
                              newSortDirection: sortBy === 'speed' && sortDirection === 'asc' ? 'desc' : 'asc'
                            });
                            
                            if (sortBy === 'speed') {
                              setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                            } else {
                              setSortBy('speed');
                              setSortDirection('asc');
                            }
                          }}
                        >
                          Speed
                          {sortBy === 'speed' && (
                            sortDirection === 'asc'
                              ? <ChevronUp className="h-3 w-3 ml-1" />
                              : <ChevronDown className="h-3 w-3 ml-1" />
                          )}
                        </button>
                        <button
                          className={`px-3 py-1.5 text-xs font-medium flex items-center ${
                            sortBy === 'type'
                              ? 'bg-purple-600 text-white'
                              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                          }`}
                          onClick={() => {
                            // Track sort change
                            trackAction('sort_change', {
                              newSortBy: 'type',
                              newSortDirection: sortBy === 'type' && sortDirection === 'asc' ? 'desc' : 'asc'
                            });
                            
                            if (sortBy === 'type') {
                              setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                            } else {
                              setSortBy('type');
                              setSortDirection('asc');
                            }
                          }}
                        >
                          Type
                          {sortBy === 'type' && (
                            sortDirection === 'asc'
                              ? <ChevronUp className="h-3 w-3 ml-1" />
                              : <ChevronDown className="h-3 w-3 ml-1" />
                          )}
                        </button>
                      </div>
                    </div>
                    
                    {/* Speed Range Filter */}
                    <div className="space-y-2 mb-4">
                      <div className="flex justify-between mb-1">
                        <label className="text-xs font-medium text-gray-300">Speed Range</label>
                        <span className="text-xs text-gray-400">
                          {speedFilter[0]} - {speedFilter[1] === 1000 ? '1000+' : speedFilter[1]} Mbps
                        </span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="1000"
                        step="10"
                        value={speedFilter[1]}
                        onChange={(e) => {
                          const newValue = parseInt(e.target.value);
                          setSpeedFilter([speedFilter[0], newValue]);
                          
                          // Track filter change
                          trackAction('filter_change', {
                            filterType: 'speed',
                            newValue: [speedFilter[0], newValue]
                          });
                        }}
                        className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-500"
                      />
                    </div>
                    
                    {/* Price Range Filter */}
                    <div className="space-y-2">
                      <div className="flex justify-between mb-1">
                        <label className="text-xs font-medium text-gray-300">Price Range</label>
                        <span className="text-xs text-gray-400">
                          R{priceFilter[0]} - R{priceFilter[1] === 2000 ? '2000+' : priceFilter[1]}
                        </span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="2000"
                        step="50"
                        value={priceFilter[1]}
                        onChange={(e) => {
                          const newValue = parseInt(e.target.value);
                          setPriceFilter([priceFilter[0], newValue]);
                          
                          // Track filter change
                          trackAction('filter_change', {
                            filterType: 'price',
                            newValue: [priceFilter[0], newValue]
                          });
                        }}
                        className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-500"
                      />
                    </div>
                  </div>
                )}
                
                {filteredPackages.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="text-gray-400 mb-2">No packages match your filters</div>
                    <button
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors"
                      onClick={() => {
                        // Track reset filters
                        trackAction('reset_filters');
                        
                        // These functions should be passed as props if needed
                        if (typeof setSpeedFilter === 'function') setSpeedFilter([0, 1000]);
                        if (typeof setPriceFilter === 'function') setPriceFilter([0, 2000]);
                      }}
                    >
                      Reset Filters
                    </button>
                  </div>
                ) : (
                  <div>
                    {/* Group packages by type */}
                    {/* Home Packages */}
                    {filteredPackages.filter(pkg => pkg.connectionType !== 'FTTB').length > 0 && (
                      <>
                        <div
                          className="flex items-center justify-between cursor-pointer mb-4"
                          onClick={() => {
                            if (isMobile) {
                              // Track section toggle
                              trackAction('toggle_section', {
                                section: 'home_packages',
                                newState: !homePackagesExpanded ? 'expanded' : 'collapsed'
                              });
                              
                              setHomePackagesExpanded(!homePackagesExpanded);
                            }
                          }}
                        >
                          <h3 className="text-xl font-semibold text-white flex items-center">
                            <Home className="h-5 w-5 text-purple-400 mr-2" />
                            Home Packages
                          </h3>
                          {isMobile && (
                            homePackagesExpanded ?
                              <ChevronUp className="h-5 w-5 text-gray-400" /> :
                              <ChevronDown className="h-5 w-5 text-gray-400" />
                          )}
                        </div>
                        
                        {(!isMobile || homePackagesExpanded) && (
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-8">
                            {filteredPackages
                              .filter(pkg => pkg.connectionType !== 'FTTB')
                              .map((pkg, index) => renderPackageCard(pkg, `home-${index}`))}
                          </div>
                        )}
                      </>
                    )}

                    {/* Business Packages */}
                    {filteredPackages.filter(pkg => pkg.connectionType === 'FTTB').length > 0 && (
                      <>
                        <div
                          className="flex items-center justify-between cursor-pointer mb-4"
                          onClick={() => {
                            if (isMobile) {
                              // Track section toggle
                              trackAction('toggle_section', {
                                section: 'business_packages',
                                newState: !businessPackagesExpanded ? 'expanded' : 'collapsed'
                              });
                              
                              setBusinessPackagesExpanded(!businessPackagesExpanded);
                            }
                          }}
                        >
                          <h3 className="text-xl font-semibold text-white flex items-center">
                            <Briefcase className="h-5 w-5 text-blue-400 mr-2" />
                            Business Packages
                          </h3>
                          {isMobile && (
                            businessPackagesExpanded ?
                              <ChevronUp className="h-5 w-5 text-gray-400" /> :
                              <ChevronDown className="h-5 w-5 text-gray-400" />
                          )}
                        </div>
                        
                        {(!isMobile || businessPackagesExpanded) && (
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                            {filteredPackages
                              .filter(pkg => pkg.connectionType === 'FTTB')
                              .map((pkg, index) => renderPackageCard(pkg, `business-${index}`))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default PackageComparisonModal;